import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sprite, SparkleBurst, PromptCapsule } from "./fx";
import { StarIcon, HourglassIcon } from "../ui-icons";

// WaitTurn — friends are lined up and the line inches forward on its own; wait
// calmly and tap ONLY when it's your turn. Faithful port of iOS
// CityHelperWaitView.

const WaitTurn = ({ payload, sounds, onDone }) => {
  const total = payload.kidsAhead.length;
  const doneRef = useRef(false);
  const [served, setServed] = useState(0);
  const [myTurn, setMyTurn] = useState(false);
  const [done, setDone] = useState(false);
  const [earlyWiggle, setEarlyWiggle] = useState(0);
  const [sparkle, setSparkle] = useState(0);
  const [serveSparkle, setServeSparkle] = useState(0);

  const ahead = Math.max(0, total - served);

  useEffect(() => {
    let cancelled = false;
    let n = 0;
    const tick = () => {
      if (cancelled) return;
      if (n < total) {
        n += 1;
        setServed(n);
        setServeSparkle((s) => s + 1);
        sounds.playPop(0.5);
        window.setTimeout(tick, 1700);
      } else {
        setMyTurn(true);
        sounds.speak("Your turn!");
      }
    };
    const id = window.setTimeout(tick, 1700);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tappedEarly = useCallback(() => {
    if (myTurn || done) return;
    sounds.playThud();
    setEarlyWiggle((w) => w + 1);
    sounds.speak("Almost! Wait for your turn.");
  }, [done, myTurn, sounds]);

  const serveMe = useCallback(() => {
    if (!myTurn || done) return;
    setDone(true);
    sounds.playPop(0.95);
    sounds.playSparkle();
    setSparkle((s) => s + 1);
    if (!doneRef.current) {
      doneRef.current = true;
      window.setTimeout(() => onDone(), 1200);
    }
  }, [done, myTurn, onDone, sounds]);

  return (
    <div className="ch-activity-body ch-wait">
      <PromptCapsule text={payload.prompt} sounds={sounds} />

      <motion.div
        className={`ch-wait-chip ${myTurn ? "is-turn" : ""}`}
        animate={earlyWiggle ? { x: [0, -8, 8, -5, 5, 0] } : {}}
        transition={{ duration: 0.4 }}
        key={earlyWiggle}
      >
        <span className="ch-wait-chip-icon" aria-hidden="true">{myTurn ? <StarIcon size={16} /> : <HourglassIcon size={16} />}</span>
        <span>
          {done ? "Yum!" : myTurn ? "It's your turn — tap!" : ahead === 1 ? "1 friend ahead…" : `${ahead} friends ahead…`}
        </span>
      </motion.div>

      <div className="ch-stage" onPointerDown={tappedEarly}>
        {myTurn && !done && <span className="ch-wait-pulse" style={{ left: "82%", top: "58%" }} />}

        <div className="ch-wait-stand" style={{ left: "82%", top: "58%" }}>
          <Sprite name={payload.standAsset || "ch_icecream"} />
        </div>

        <AnimatePresence>
          {payload.kidsAhead.map((asset, i) =>
            i >= served ? (
              <motion.div
                key={i}
                className="ch-wait-kid"
                initial={false}
                animate={{ left: `${82 - (i - served + 1) * 16}%`, opacity: 1, scale: 1 }}
                exit={{ left: "82%", opacity: 0, scale: 0.9 }}
                transition={{ type: "spring", stiffness: 120, damping: 16 }}
                style={{ top: "58%" }}
              >
                <span className="ch-wait-kid-shadow" />
                <Sprite name={asset} style={{ animationDelay: `${i * 0.35}s` }} />
              </motion.div>
            ) : null
          )}
        </AnimatePresence>

        <SparkleBurst trigger={serveSparkle} reach={60} style={{ left: "82%", top: "52%" }} />
        <SparkleBurst trigger={sparkle} reach={120} style={{ left: "82%", top: "50%" }} />

        <AnimatePresence>
          {myTurn && !done && (
            <motion.button
              type="button"
              className="ch-wait-btn"
              style={{ left: "50%", top: "88%" }}
              onClick={(e) => {
                e.stopPropagation();
                serveMe();
              }}
              onPointerDown={(e) => e.stopPropagation()}
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.4, opacity: 0 }}
            >
              My scoop!
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default WaitTurn;
